import type { WorkflowTrace } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import type {
    MutableBranchStepKey,
    MutableLoopStepKey,
    MutableTickTracePoint,
    MutableToolStepKey,
    MutableWorkflowState,
} from '@toolproof-core/lib/_contract/workflow-overlay';

export function isWorkflowTrace(value: unknown): value is WorkflowTrace {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
    const maybe = value as Record<string, unknown>;
    return typeof maybe.handle === 'string' && Array.isArray(maybe.tracePoints);
}

export function toBranchCaseAddressKey(args: {
    ownerBranchStepKey: MutableBranchStepKey;
    ownerCaseIndex: number;
    slotInOwner: 'cases/when' | 'cases/what';
}): string {
    const { ownerBranchStepKey, ownerCaseIndex, slotInOwner } = args;
    return `${ownerBranchStepKey}|${ownerCaseIndex}|${slotInOwner}`;
}

export function mergeWorkflowStateDelta(args: {
    workflowState: MutableWorkflowState;
    workflowStateDelta?: Partial<MutableWorkflowState>;
}): MutableWorkflowState {
    const { workflowState, workflowStateDelta } = args;
    if (!workflowStateDelta) return workflowState;

    const nextWorkflowState: MutableWorkflowState = { ...workflowState };
    for (const [toolStepKey, workflowStateEntryByRoleName] of Object.entries(workflowStateDelta)) {
        const typedToolStepKey = toolStepKey as keyof MutableWorkflowState;
        const nextRoleEntries = {
            ...((nextWorkflowState[typedToolStepKey] ?? {}) as Record<string, unknown>),
            ...((workflowStateEntryByRoleName ?? {}) as Record<string, unknown>),
        };
        nextWorkflowState[typedToolStepKey] = nextRoleEntries as MutableWorkflowState[keyof MutableWorkflowState];
    }

    return nextWorkflowState;
}

export function isLoopStepKey(stepKey: string | null | undefined): stepKey is MutableLoopStepKey {
    return typeof stepKey === 'string'
        && (stepKey.startsWith('WHILE_STEP-') || stepKey.startsWith('FOR_STEP-'));
}

export function deriveRootLoopStepKeyByCloneLoopStepKey(args: {
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
}): Map<MutableLoopStepKey, MutableLoopStepKey> {
    const { tickTracePoints, appliedTickCount } = args;
    const rootLoopStepKeyByCloneLoopStepKey = new Map<MutableLoopStepKey, MutableLoopStepKey>();

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.loop_continuation) continue;
        if (tracePoint.stepKind !== CONSTANTS.Enums.StepKind.while && tracePoint.stepKind !== CONSTANTS.Enums.StepKind.for) continue;

        const ownerLoopStepKey = tracePoint.stepKey;
        if (!isLoopStepKey(ownerLoopStepKey)) continue;

        const rootLoopStepKey = rootLoopStepKeyByCloneLoopStepKey.get(ownerLoopStepKey) ?? ownerLoopStepKey;
        rootLoopStepKeyByCloneLoopStepKey.set(ownerLoopStepKey, rootLoopStepKey);

        const insertedSteps = tracePoint.delta?.stepsMutation?.insertedSteps ?? [];
        const insertedLoopClone = insertedSteps.find((insertedStep) => (
            (insertedStep.stepKind === CONSTANTS.Enums.StepKind.while || insertedStep.stepKind === CONSTANTS.Enums.StepKind.for)
            && isLoopStepKey(insertedStep.stepKey)
        ));
        if (!insertedLoopClone) continue;

        rootLoopStepKeyByCloneLoopStepKey.set(
            insertedLoopClone.stepKey as MutableLoopStepKey,
            rootLoopStepKey,
        );
    }

    return rootLoopStepKeyByCloneLoopStepKey;
}

export function normalizeLoopOwnerStepKey(args: {
    ownerStepKey: MutableBranchStepKey | MutableLoopStepKey | null | undefined;
    rootLoopStepKeyByCloneLoopStepKey: ReadonlyMap<MutableLoopStepKey, MutableLoopStepKey>;
}): MutableLoopStepKey | null {
    const { ownerStepKey, rootLoopStepKeyByCloneLoopStepKey } = args;
    if (!isLoopStepKey(ownerStepKey)) return null;
    return rootLoopStepKeyByCloneLoopStepKey.get(ownerStepKey) ?? ownerStepKey;
}

export function deriveRuntimeToolStepOwnerByToolStepKey(args: {
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
}): Map<MutableToolStepKey, MutableBranchStepKey | MutableLoopStepKey> {
    const { tickTracePoints, appliedTickCount } = args;
    const runtimeToolStepOwnerByToolStepKey = new Map<MutableToolStepKey, MutableBranchStepKey | MutableLoopStepKey>();

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.tool_execution) continue;
        if (tracePoint.stepKind !== CONSTANTS.Enums.StepKind.tool) continue;

        const ownerMacroStepKey = tracePoint.macroStepContext.ownerMacroStepKey;
        if (typeof ownerMacroStepKey !== 'string') continue;
        if (
            !ownerMacroStepKey.startsWith('BRANCH_STEP-')
            && !ownerMacroStepKey.startsWith('WHILE_STEP-')
            && !ownerMacroStepKey.startsWith('FOR_STEP-')
        ) {
            continue;
        }

        runtimeToolStepOwnerByToolStepKey.set(
            tracePoint.stepKey as MutableToolStepKey,
            ownerMacroStepKey as MutableBranchStepKey | MutableLoopStepKey
        );
    }

    return runtimeToolStepOwnerByToolStepKey;
}
